import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';

export default function AkutHjaelp() {
  const { lang } = useLanguage();

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Akut hjælp | Nordisk Psykiatri' 
    : 'Akut hjälp | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Nordisk Psykiatri er ikke en akut psykiatrisk funktion. Se her hvor du kan få akut hjælp ved akutte kriser og henvendelser.'
    : 'Nordisk Psykiatri är inte en akut psykiatrisk funktion. Se här var du kan få akut hjälp vid akuta kriser och behov.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const content = {
    da: {
      title: 'Akut hjælp',
      intro: 'Nordisk Psykiatri er ikke en akut psykiatrisk funktion. Hvis du har brug for akut hjælp, henviser vi til nedenstående muligheder.',
      items: [
        {
          icon: 'fa-light fa-phone',
          title: 'Ring 112',
          text: 'Ved livstruende situationer eller ved akut fare for dig selv eller andre.'
        },
        {
          icon: 'fa-light fa-user-doctor',
          title: 'Kontakt din egen læge',
          text: 'Ved akut opståede psykiske eller fysiske symptomer i dagtimerne.'
        },
        {
          icon: 'fa-light fa-hospital',
          title: 'Lægevagten – 1813',
          text: 'Ved behov for lægelig vurdering uden for din egen læges åbningstid.'
        },
        {
          icon: 'fa-light fa-house-medical',
          title: 'Psykiatrisk akutmodtagelse',
          text: 'Ved behov for akut psykiatrisk vurdering kan du kontakte din regionale psykiatriske akutmodtagelse.'
        }
      ],
      importantTitle: 'Vigtigt',
      importantText: (
        <span>
          Ved tanker om selvmord kan du kontakte Livslinjen på{' '}
          <a href="tel:70201201" className="font-medium underline hover:text-red-800 transition-colors">
            70 20 12 01
          </a>{' '}
          eller skrive anonymt på{' '}
          <a href="https://www.livslinjen.dk" target="_blank" rel="noopener noreferrer" className="font-medium underline hover:text-red-800 transition-colors">
            livslinjen.dk
          </a>
          .
        </span>
      )
    },
    sv: {
      title: 'Akut hjälp',
      intro: 'Nordisk Psykiatri är inte en akut psykiatrisk funktion. Om du är i behov av akut hjälp hänvisar vi till alternativen nedan.',
      items: [
        {
          icon: 'fa-light fa-phone',
          title: 'Ring 112',
          text: 'Vid livshotande situationer eller vid akut fara för dig själv eller andra.'
        },
        {
          icon: 'fa-light fa-user-doctor',
          title: 'Kontakta din egen läkare',
          text: 'Vid akut uppkomna psykiska eller fysiska symptom under dagtid.'
        },
        {
          icon: 'fa-light fa-hospital',
          title: 'Lægevagten – 1813',
          text: 'Vid behov av medicinsk bedömning utanför din egen läkares öppettider.'
        },
        {
          icon: 'fa-light fa-house-medical',
          title: 'Psykiatrisk akutmottagning',
          text: 'Vid behov av akut psykiatrisk bedömning kan du kontakta din regionala psykiatriska akutmottagning.'
        }
      ],
      importantTitle: 'Viktigt',
      importantText: (
        <span>
          Vid tankar på självmord kan du kontakta Livslinjen på{' '}
          <a href="tel:70201201" className="font-medium underline hover:text-red-800 transition-colors">
            70 20 12 01
          </a>{' '}
          eller skriva anonymt på{' '}
          <a href="https://www.livslinjen.dk" target="_blank" rel="noopener noreferrer" className="font-medium underline hover:text-red-800 transition-colors">
            livslinjen.dk
          </a>
          .
        </span>
      )
    }
  };

  const t = content[lang] || content.da;

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* HERO */}
      <FluidSubpageHero
        className="bg-[#f6f0e2]"
        style={{ backgroundColor: '#f6f0e2' }}
        eyebrow={lang === 'da' ? 'AKUT HJÆLP' : 'AKUT HJÄLP'}
        title={t.title}
        description={t.intro}
      />

      <section className="py-12 md:py-16 bg-sand-light">
        <div className="mx-auto max-w-4xl px-6">
          {/* Items with HR between */}
          <div className="space-y-10">
            {t.items.map((item, idx) => (
              <React.Fragment key={idx}>
                <div className="flex items-start gap-6 text-left">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-petrol/5 flex items-center justify-center text-petrol text-xl" aria-hidden="true">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-light text-petrol-dark mb-2">
                      {item.title}
                    </h2>
                    <p className="text-base text-[#2d3237] leading-relaxed font-light">
                      {item.text}
                    </p>
                  </div>
                </div>
                <hr className="border-[#ece8e1]" />
              </React.Fragment>
            ))}
          </div>

          {/* Important Box */}
          <div className="mt-10 bg-red-50/60 border border-red-100 rounded-lg p-6 text-left">
            <h3 className="font-display text-xl font-medium text-red-950 mb-2">
              {t.importantTitle}
            </h3>
            <p className="text-base text-red-900/90 leading-relaxed font-light">
              {t.importantText}
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}
